import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { X } from '@/components/icons';
import { apiError } from '@/lib/apiError';
import {
  MAX_PICK_PHOTOS, pickListPhotos, removePickListPhoto, uploadPickListPhotos, type PickList,
} from '@/api/pickList';

/**
 * Photos of the segregated items on a Pick List — upload, drag in, or paste (Ctrl+V)
 * up to 5. Each change saves straight away; they show in Packing and print on the
 * Packing Scan Receipt and the Dispatch Note.
 */
export function PickListPhotos({ pickList, active = true, compact }: {
  pickList: Pick<PickList, 'id' | 'photoUrls'>;
  /** Listen for Ctrl+V pastes only while this panel is on screen. */
  active?: boolean;
  /** Smaller tiles, for use inside the Pick List form. */
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [photos, setPhotos] = useState<string[]>(() => pickListPhotos(pickList));
  useEffect(() => { setPhotos(pickListPhotos(pickList)); }, [pickList.id, pickList.photoUrls]);
  const left = MAX_PICK_PHOTOS - photos.length;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['pick-list'] });
    void qc.invalidateQueries({ queryKey: ['packing'] });
  };
  const upload = useMutation({
    mutationFn: (files: (File | Blob)[]) => uploadPickListPhotos(pickList.id, files),
    onSuccess: (r, files) => {
      setPhotos(r.photoUrls);
      refresh();
      toast.push(`${files.length} photo${files.length === 1 ? '' : 's'} added`, 'success');
    },
    onError: (e) => toast.push(apiError(e, 'Could not upload photo'), 'error'),
  });
  const remove = useMutation({
    mutationFn: (url: string) => removePickListPhoto(pickList.id, url),
    onSuccess: (r) => { setPhotos(r.photoUrls); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Could not remove photo'), 'error'),
  });

  function take(list: (File | Blob)[]) {
    const images = list.filter((f) => f.type?.startsWith('image/'));
    if (!images.length) { toast.push('Please choose an image file', 'error'); return; }
    if (left <= 0) { toast.push(`Up to ${MAX_PICK_PHOTOS} photos — remove one first`, 'error'); return; }
    if (images.length > left) toast.push(`Only ${left} more photo${left === 1 ? '' : 's'} allowed — adding the first ${left}`, 'warning');
    upload.mutate(images.slice(0, left));
  }

  // Paste images (Ctrl+V) anywhere while the panel is shown; text pastes pass through.
  useEffect(() => {
    if (!active) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.items ?? [])]
        .filter((it) => it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
      if (files.length) { e.preventDefault(); take(files); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, left, pickList.id]);

  const tile = compact ? 'h-16 w-20' : 'aspect-[4/3]';
  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); take([...(e.dataTransfer?.files ?? [])]); }}
        className={`${compact ? 'flex flex-wrap items-center' : 'grid grid-cols-2 sm:grid-cols-3'} gap-2 rounded-lg border border-dashed p-2 ${dragOver ? 'border-accent ring-2 ring-accent/40' : 'border-[var(--border-color)]'}`}
      >
        {photos.map((url, i) => (
          <div key={url} className={`relative ${tile} overflow-hidden rounded-md border border-[var(--border-color)]`}>
            <a href={url} target="_blank" rel="noreferrer" title="Open full size">
              <img src={url} alt={`Segregated items photo ${i + 1}`} className="h-full w-full object-cover" />
            </a>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={async () => { if (await confirmDlg('Remove this photo?')) remove.mutate(url); }}
              className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-danger"
              aria-label="Remove photo"
              title="Remove photo"
            >
              <X size={11} />
            </button>
          </div>
        ))}
        {left > 0 && (
          <button
            type="button"
            disabled={upload.isPending}
            onClick={() => fileRef.current?.click()}
            className={`flex ${compact ? 'h-16 min-w-[80px] px-2' : 'aspect-[4/3]'} flex-col items-center justify-center rounded-md border border-[var(--border-color)] text-[11px] font-semibold text-[var(--text-secondary)] hover:border-accent hover:text-accent`}
          >
            <span className="text-base leading-none">＋</span>
            {upload.isPending ? 'Uploading…' : 'Add photos'}
          </button>
        )}
        {compact && <span className="text-[11px] text-[var(--text-muted)]">Upload, drag in, or paste (Ctrl+V) — saves straight away.</span>}
      </div>
      {!compact && (
        <p className="mt-2 text-[11px] text-[var(--text-muted)]">
          Upload, drag in, or paste (Ctrl+V) up to {MAX_PICK_PHOTOS} photos of the segregated items. They save straight away, show in Packing and print on the Packing receipt and Dispatch Note.
        </p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => { take([...(e.target.files ?? [])]); e.target.value = ''; }}
      />
    </div>
  );
}

/** "Photos" button on a Pick List row → add or remove segregated-items photos. */
export function PickListPhotosModal({ pickList, onClose }: { pickList: PickList | null; onClose: () => void }) {
  return (
    <Modal
      open={!!pickList}
      onClose={onClose}
      size="md"
      title={`Segregated items photos — ${pickList?.pickNo ?? ''}`}
      footer={<Button variant="glass" onClick={onClose}>Done</Button>}
    >
      {pickList && <PickListPhotos pickList={pickList} />}
    </Modal>
  );
}

/** Read-only strip of a Pick List's photos (Packing) — click one to open it full size. */
export function PickListPhotoStrip({ photoUrls }: { photoUrls?: string | null }) {
  const photos = pickListPhotos({ photoUrls });
  if (!photos.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {photos.map((u, i) => (
        <a key={u} href={u} target="_blank" rel="noreferrer" title={`Photo ${i + 1} — open full size`}>
          <img src={u} alt={`Segregated items photo ${i + 1}`} className="h-16 w-20 rounded-md border border-[var(--border-color)] object-cover" />
        </a>
      ))}
    </div>
  );
}
